import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { guardarConfirmado, reportarErrorGuardado, consultarEstadoIncierto } from "@/lib/guardado";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, ShieldAlert } from "lucide-react";
import { Field, YesNoSelect, HybridSelect, DateField } from "@/components/forms/fields";
import ContactForm from "@/components/forms/ContactForm";
import PhoneInput from "@/components/forms/PhoneInput";
import { useAuth } from "@/context/AuthContext";
import { capacidades } from "@/lib/capacidades";

const CONDICIONES_CAT = [
  ["dificultad_auditiva", "Dificultad auditiva"], ["dificultad_para_hablar", "Dificultad para hablar"],
  ["deterioro_cognitivo", "Deterioro cognitivo"], ["movilidad_reducida", "Movilidad reducida"],
  ["usa_baston_o_andadera", "Usa bastón/andadera"], ["no_puede_levantarse_solo", "No puede levantarse solo"],
  ["oxigeno_suplementario", "Oxígeno suplementario"], ["vive_solo", "Vive solo"],
  ["tiene_mascota", "Tiene mascota"], ["otra", "Otra"],
];

const defaultInstr = () => ({
  autoridad: { tipo: "", nombre: "", relacion: "" },
  autorizacion_emergencia: { valor: null, fecha: "" },
  instruccion_rechazo_emergencia: "",
  directiva_anticipada: { existe: null, responsable: "", instruccion_operativa: "" },
  quien_modifica_protocolo: { contacto_id: null, nombre: "", telefono: "" },
  conflicto_familiares: { contacto_id: null, nombre: "", telefono: "" },
  verificador_fisico: { contratado: null, autoriza: { contacto_id: null, nombre: "", telefono: "" } },
  acceso_inmueble: { ubicacion_codigo_caja: "", caseta: "", puertas_adicionales: "", mascotas: "", obstaculos: "" },
  consentimiento_audio_bidireccional: { valor: null, fecha: "" },
  consentimiento_grabacion: { valor: null, fecha: "" },
  confirmacion_limites: { valor: null, fecha: "" },
});

const emptyPersona = () => ({
  nombre_completo: "",
  fecha_nacimiento: "",
  telefono: "",
  idioma_preferido: "Español",
  condiciones: "",
  paquete: "",
  estado: "Activa",
  clientes: [],
  contactos: [],
  servicios_contratados: [],
  politica_recordatorios: {},
  instrucciones: defaultInstr(),
});

// v2.13.4 — alcance de servicio (espeja shared.SERVICIOS_CONTRATADOS del backend).
const SERVICIOS = [
  ["recordatorios", "Recordatorios programados", "Llamada operativa en la hora acordada. No requiere dispositivos."],
  ["monitoreo_sensores", "Monitoreo con sensores", "Requiere dispositivos instalados y cobertura confirmada."],
];

// Control Sí/No + fecha
function YesNoFecha({ obj, onChange, testId }) {
  const o = obj || { valor: null, fecha: "" };
  return (
    <div className="grid grid-cols-2 gap-3">
      <YesNoSelect value={o.valor} onChange={(v) => onChange({ ...o, valor: v })} testId={testId} />
      <DateField value={o.fecha} onChange={(v) => onChange({ ...o, fecha: v })} testId={testId ? `${testId}-fecha` : undefined} />
    </div>
  );
}

export function etiquetaCama(c, zonas = []) {
  const z = (zonas || []).find((x) => x.id === c.zona_id);
  return `${c.etiqueta || c.nombre || "Cama"}${z ? ` · ${z.nombre}` : ""}`;
}

export default function PersonForm({ open, onOpenChange, persona, sitioId, dispositivos, clientes, contactos, onSaved, refetchContactos, sections, title, puedeAsociarDispositivos = true, puedeCrearContacto = true, modalidad = "residencial", camas = [], zonas = [] }) {
  const esInstitucional = modalidad === "institucional";
  const { user } = useAuth();
  const caps = capacidades(user);
  const secs = (sections && sections.length ? sections : ["datos", "contactos", "instrucciones", "dispositivos"])
    .filter((s) => s !== "dispositivos" || puedeAsociarDispositivos);
  const [f, setF] = useState(emptyPersona());
  const [selDevices, setSelDevices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [contactoDlg, setContactoDlg] = useState(false);
  // Contactos creados desde este formulario: quedan disponibles para vincular de inmediato,
  // sin esperar a que la lista global se vuelva a consultar (UX-002).
  const [contactosNuevos, setContactosNuevos] = useState([]);
  // Contactos VINCULADOS a esta persona (lectura contextual, no catálogo global).
  const [vinculados, setVinculados] = useState([]);
  const [errorContactos, setErrorContactos] = useState("");
  // FIX 02 (ajuste): intención EXPLÍCITA de registrar/confirmar el contratante principal en esta
  // edición. Arranca en false y sólo la enciende el bloque contratante.
  const [contratanteConfirmado, setContratanteConfirmado] = useState(false);

  const [errorGuardado, setErrorGuardado] = useState(null);
  const enviandoRef = useRef(false);
  const cargadoRef = useRef(null);

  const opcionesContactos = [
    // MINIMIZACIÓN (v2.13.4): el expediente sólo conoce los contactos VINCULADOS a esta persona
    // (leídos de /api/personas/{id}/contactos) más los creados aquí mismo. No se descarga ni se
    // ofrece el catálogo global: los contactos de otras personas nunca llegan al navegador.
    ...vinculados.map((r) => r.contacto).filter(Boolean),
    ...contactosNuevos.filter((n) => !vinculados.some((r) => r.contacto_id === n.id)),
  ];

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleCond = (v) => setF((p) => {
    const s = new Set(p.condiciones_catalogo || []);
    s.has(v) ? s.delete(v) : s.add(v);
    return { ...p, condiciones_catalogo: [...s] };
  });
  const setInstr = (k, v) => setF((p) => ({ ...p, instrucciones: { ...p.instrucciones, [k]: v } }));

  useEffect(() => {
    if (!open) { cargadoRef.current = null; return; }
    // La captura en curso NO se reinicia porque el padre vuelva a consultar personas, contactos o
    // dispositivos (antes esa recarga borraba las filas de contacto recién agregadas).
    const clave = persona?.id || "nuevo";
    if (cargadoRef.current === clave) return;
    cargadoRef.current = clave;
    setContactosNuevos([]);
    setErrorContactos("");
    setContratanteConfirmado(false);
    if (persona) {
      setF({
        ...emptyPersona(),
        ...persona,
        instrucciones: { ...defaultInstr(), ...(persona.instrucciones || {}) },
        clientes: persona.clientes || [],
        contactos: persona.contactos || [],
        servicios_contratados: persona.servicios_contratados || [],
        politica_recordatorios: persona.politica_recordatorios || {},
      });
      const assigned = (dispositivos || []).filter((d) => (d.personas_monitoreadas || []).includes(persona.id)).map((d) => d.id);
      setSelDevices(assigned);
    } else {
      setF(emptyPersona());
      setSelDevices([]);
    }
  }, [open, persona, dispositivos]);

  // v2.13.4 — los contactos del expediente se leen SÓLO de esta persona.
  useEffect(() => {
    if (!open || !persona?.id) { setVinculados([]); return; }
    let vivo = true;
    api.get(`/personas/${persona.id}/contactos`)
      .then(({ data }) => { if (vivo) setVinculados(data.contactos || []); })
      .catch(() => { if (vivo) setVinculados([]); });
    return () => { vivo = false; };
  }, [open, persona?.id]);

  // Contacto creado DENTRO del expediente: el backend ya lo vinculó; se refleja de inmediato.
  const contactoCreadoEnPersona = (nuevo, relaciones) => {
    if (!nuevo?.id) return;
    setContactosNuevos((n) => [...n.filter((c) => c.id !== nuevo.id), nuevo]);
    setVinculados((v) => [...v, { contacto_id: nuevo.id, orden: v.length + 1, contacto: nuevo }]);
    if (relaciones) setF((p) => ({ ...p, contactos: relaciones }));
    setErrorContactos("");
  };

  // ---- Contactos de escalamiento ----
  // Todas las mutaciones usan el actualizador funcional: dos cambios en el mismo ciclo (elegir un
  // contacto y agregar otra fila) ya no se pisan entre sí (DATA-011).
  const addContactoRow = () =>
    setF((p) => ({
      ...p,
      contactos: [...(p.contactos || []), { contacto_id: "", relacion_familiar: "", orden: (p.contactos?.length || 0) + 1, autoridad: "" }],
    }));
  const updContactoRow = (i, k, v) =>
    setF((p) => ({ ...p, contactos: (p.contactos || []).map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const delContactoRow = (i) =>
    setF((p) => ({ ...p, contactos: (p.contactos || []).filter((_, idx) => idx !== i) }));

  // Contacto creado desde la persona: se vincula solo a la primera fila libre (o crea una nueva).
  const vincularContactoNuevo = (nuevo) => {
    if (!nuevo?.id) return;
    setContactosNuevos((n) => [...n.filter((c) => c.id !== nuevo.id), nuevo]);
    setF((p) => {
      const filas = [...(p.contactos || [])];
      const ya = filas.some((r) => r.contacto_id === nuevo.id);
      if (ya) return p;
      const libre = filas.findIndex((r) => !r.contacto_id);
      if (libre >= 0) filas[libre] = { ...filas[libre], contacto_id: nuevo.id };
      else filas.push({ contacto_id: nuevo.id, relacion_familiar: "", orden: filas.length + 1, autoridad: "" });
      return { ...p, contactos: filas };
    });
    setErrorContactos("");
    refetchContactos && refetchContactos();
  };

  // ---- Cliente relación (una) ----
  const cliRel = f.clientes?.[0] || { cliente_id: "", contacto_id: "", relacion_familiar: "", es_contratante_principal: false };
  const propiaPersonaPaga = !!cliRel.es_la_propia_persona;
  // FIX 02 (ajuste): la principalidad SÓLO se escribe si el operador la confirma explícitamente en
  // este bloque —seleccionando el contratante o pulsando «Confirmar»—. Guardar un teléfono, unas
  // instrucciones u otro dato no relacionado conserva la relación y su principalidad tal cual está.
  const setCliRel = (patch, { confirma = false } = {}) => {
    if (confirma) setContratanteConfirmado(true);
    setF((p) => {
      const resto = (p.clientes || []).slice(1);
      const actual = p.clientes?.[0] || { cliente_id: "", relacion_familiar: "", es_contratante_principal: false };
      return { ...p, clientes: [{ ...actual, ...patch }, ...resto] };
    });
  };
  const contratantePrincipalListo = !!cliRel.es_contratante_principal || contratanteConfirmado;

  const toggleDevice = (id) =>
    setSelDevices((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // ---- v2.13.4 · Alcance de servicio y política de no respuesta de Recordatorios ----
  const servicios = f.servicios_contratados || [];
  const soloRecordatorios = servicios.includes("recordatorios") && !servicios.includes("monitoreo_sensores");
  const toggleServicio = (v) => setF((p) => {
    const s = new Set(p.servicios_contratados || []);
    s.has(v) ? s.delete(v) : s.add(v);
    return { ...p, servicios_contratados: [...s] };
  });
  const pol = f.politica_recordatorios || {};
  const setPol = (k, v) => setF((p) => ({ ...p, politica_recordatorios: { ...(p.politica_recordatorios || {}), [k]: v } }));

  const save = async () => {
    if (enviandoRef.current) return; // guard sincrónico: un doble clic no dispara dos escrituras
    setErrorGuardado(null);
    // Una fila de contacto sin vínculo NO se descarta en silencio con un mensaje de éxito
    // (FUNC-003): se avisa para seleccionarlo o eliminarlo.
    const incompletas = (f.contactos || []).filter((c) => !c.contacto_id).length;
    if (incompletas > 0) {
      setErrorContactos(
        incompletas === 1
          ? "Hay un contacto sin seleccionar. Elige el contacto o elimina la fila."
          : `Hay ${incompletas} contactos sin seleccionar. Elígelos o elimina las filas.`);
      toast.error("Falta seleccionar el contacto en una de las filas.");
      return;
    }
    setErrorContactos("");
    enviandoRef.current = true;
    setSaving(true);
    try {
      const payload = {
        ...f,
        sitio_id: sitioId,
        // FIX 02 (ajuste): sólo una CONFIRMACIÓN EXPLÍCITA del bloque contratante escribe
        // es_contratante_principal=true. Sin confirmación la relación viaja intacta (incluida una
        // legada con false) y las demás relaciones se conservan; sin contratante seleccionado no se
        // crea ni se marca ninguno.
        clientes: (cliRel.cliente_id || cliRel.contacto_id || cliRel.es_la_propia_persona)
          ? [contratanteConfirmado ? { ...cliRel, es_contratante_principal: true } : cliRel,
             ...(f.clientes || []).slice(1)]
          : (f.clientes || []).slice(1),
        contactos: (f.contactos || []).map((c, i) => ({ ...c, contacto: undefined, orden: c.orden || i + 1 })),
      };
      // G-03/G-04: en EDICIÓN el estado no viaja en el PUT genérico (el backend lo rechaza): el
      // lifecycle se cambia sólo con la acción dedicada de activar/desactivar. En ALTA se conserva
      // la semántica vigente (el POST sí define el estado inicial).
      if (persona?.id) delete payload.estado;
      const escribir = async () => {
        const res = persona?.id
          ? await api.put(`/personas/${persona.id}`, payload)
          : await api.post("/personas", payload);
        const personaId = res.data.id;
        // La asociación persona<->dispositivo se escribe con PUT /api/dispositivos, que exige
        // supervisor: si el actor no puede asociarlos no se intenta (evitaría un 403 DESPUÉS de
        // haber guardado la persona, que se leería como "no se guardó").
        if (!puedeAsociarDispositivos) return res;
        // Sincronizar asignación de dispositivos
        const updates = [];
        (dispositivos || []).forEach((d) => {
          const has = (d.personas_monitoreadas || []).includes(personaId);
          const should = selDevices.includes(d.id);
          if (has !== should) {
            const nuevos = should
              ? [...(d.personas_monitoreadas || []), personaId]
              : (d.personas_monitoreadas || []).filter((x) => x !== personaId);
            updates.push(api.put(`/dispositivos/${d.id}`, { ...d, personas_monitoreadas: nuevos }));
          }
        });
        if (updates.length) await Promise.all(updates);
        return res;
      };

      // UI-012: el éxito se anuncia después de que la ficha se refresca con lo confirmado.
      await guardarConfirmado({
        escribir,
        refrescar: (res) => (onSaved ? onSaved(res.data) : undefined),
        exito: "Persona monitoreada guardada.",
      });
      onOpenChange(false);
    } catch (e) {
      setErrorGuardado(reportarErrorGuardado(e, { consultarEstado: onSaved }));
    } finally {
      enviandoRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#012472]">
            {title || (persona?.id ? "Editar persona monitoreada" : "Nueva persona monitoreada")}
          </DialogTitle>
        </DialogHeader>

        <Accordion type="multiple" defaultValue={secs} className="w-full">
          {/* 1. Datos */}
          {secs.includes("datos") && (<AccordionItem value="datos">
            <AccordionTrigger className="text-[#012472]">Datos de la persona</AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <Field label="Nombre completo">
                <Input data-testid="persona-nombre-input" className="rounded-xl" value={f.nombre_completo} onChange={(e) => set("nombre_completo", e.target.value)} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nombre de trato (opcional)">
                  <Input className="rounded-xl" value={f.nombre_trato || ""} onChange={(e) => set("nombre_trato", e.target.value)} />
                </Field>
                <Field label="Fecha de nacimiento">
                  <DateField testId="persona-fecha-nacimiento" value={f.fecha_nacimiento} onChange={(v) => set("fecha_nacimiento", v)} />
                </Field>
              </div>
              <Field label="Teléfono propio (internacional)">
                <PhoneInput testId="persona-telefono" value={f} onChange={(t) => setF((p) => ({ ...p, ...t }))} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Idioma preferido">
                  <Select value={f.idioma_preferido} onValueChange={(v) => set("idioma_preferido", v)}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Español">Español</SelectItem>
                      <SelectItem value="Inglés">Inglés</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Paquete contratado">
                  <Select value={f.paquete || ""} onValueChange={(v) => set("paquete", v)}>
                    <SelectTrigger data-testid="persona-paquete-select" className="rounded-xl"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Básico">Básico</SelectItem>
                      <SelectItem value="Premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Condiciones que afectan la verificación" hint="Sólo lo que cambia cómo se verifica/atiende. NO es historia clínica.">
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  {CONDICIONES_CAT.map(([v, l]) => (
                    <label key={v} className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox data-testid={`persona-cond-${v}`} checked={(f.condiciones_catalogo || []).includes(v)} onCheckedChange={() => toggleCond(v)} />
                      {l}
                    </label>
                  ))}
                </div>
                <Textarea className="rounded-xl" placeholder="Nota corta adicional (opcional)" value={f.condiciones} onChange={(e) => set("condiciones", e.target.value)} />
              </Field>
              {/* UI-014/UI-015: la ubicación interna sólo existe en un sitio institucional; en
                  residencial no aparece lenguaje institucional (el dato capturado no se borra).
                  UI-006: la cama se ELIGE de las camas registradas del sitio (relación real
                  persona.cama_id → cama.zona_id), no se adivina por el texto. */}
              {esInstitucional && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Habitación / cama (referencia)">
                    <Input data-testid="persona-habitacion-input" className="rounded-xl" placeholder="Ej. Hab. 204 · Cama A" value={f.habitacion || ""} onChange={(e) => set("habitacion", e.target.value)} />
                  </Field>
                  <Field label="Cama registrada del sitio">
                    <Select value={f.cama_id || "__sin__"} onValueChange={(v) => set("cama_id", v === "__sin__" ? null : v)}>
                      <SelectTrigger className="rounded-xl" data-testid="persona-cama-select"><SelectValue placeholder="Sin cama asignada" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__sin__">Sin cama asignada</SelectItem>
                        {(camas || []).filter((c) => c.estado !== "Inactivo").map((c) => (
                          <SelectItem key={c.id} value={c.id}>{etiquetaCama(c, zonas)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(camas || []).filter((c) => c.estado !== "Inactivo").length === 0 && (
                      <p className="text-xs text-slate-400 mt-1">Aún no hay camas registradas. Se agregan en Espacios del sitio.</p>
                    )}
                  </Field>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contratante / pagador">
                  {/* v2.13.4 — el catálogo GLOBAL de contratantes sólo se ofrece a la
                      administración (supervisor). En el expediente operativo se declara si la
                      propia persona contrata/paga, sin obligar a elegir a un tercero ajeno, y el
                      contratante ya registrado se conserva intacto. */}
                  {propiaPersonaPaga ? (
                    <p data-testid="contratante-propia-persona-activo" className="text-sm text-slate-600">La propia persona contrata y paga el servicio.</p>
                  ) : (
                    // v2.13.4 — el expediente NO navega entidades ajenas: el pagador es la propia
                    // persona o uno de SUS contactos ya vinculados. Si es un tercero, primero se
                    // crea/vincula como contacto y después se elige aquí. Billing sin rediseñar.
                    <Select value={cliRel.contacto_id || ""} onValueChange={(v) => setCliRel({ contacto_id: v, cliente_id: null, es_la_propia_persona: false }, { confirma: true })}>
                      <SelectTrigger className="rounded-xl" data-testid="select-contratante-contacto"><SelectValue placeholder="Elige entre sus contactos" /></SelectTrigger>
                      <SelectContent>
                        {opcionesContactos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre_completo || "(sin nombre)"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {!propiaPersonaPaga && cliRel.cliente_id && (
                    <p data-testid="contratante-legacy" className="mt-1 text-xs text-slate-400">Contratante registrado antes en el catálogo; se conserva si no eliges otro.</p>
                  )}
                </Field>
                <Field label="Parentesco con la persona">
                  <Input className="rounded-xl" placeholder="Ej. Hijo, Hija" value={cliRel.relacion_familiar} onChange={(e) => setCliRel({ relacion_familiar: e.target.value })} />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox data-testid="contratante-propia-persona" checked={propiaPersonaPaga}
                  onCheckedChange={(v) => (v
                    ? setCliRel({ es_la_propia_persona: true, cliente_id: null, relacion_familiar: cliRel.relacion_familiar || "Titular" }, { confirma: true })
                    : setCliRel({ es_la_propia_persona: false }))} />
                La propia persona contrata y paga el servicio
              </label>
              {/* FIX 02 (ajuste): confirmación mínima DENTRO del bloque contratante. No es una
                  casilla decorativa: decide si el guardado escribe la principalidad. Permite
                  confirmar una relación legada sin cambiar el parentesco ni elegir a otra persona,
                  y no condiciona el guardado del resto de la ficha. */}
              {(cliRel.cliente_id || cliRel.contacto_id || propiaPersonaPaga) ? (
                contratantePrincipalListo ? (
                  <p data-testid="contratante-principal-ok" className="text-xs text-emerald-700">
                    {contratanteConfirmado && !cliRel.es_contratante_principal
                      ? "Contratante principal confirmado: se guardará al guardar la ficha."
                      : "Registrado como contratante principal de la persona."}
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" size="sm" className="rounded-lg"
                      data-testid="confirmar-contratante-principal"
                      onClick={() => setContratanteConfirmado(true)}>
                      Confirmar como contratante principal
                    </Button>
                    <span className="text-xs text-slate-500">
                      Esta relación aún no está marcada como contratante principal. Guardar otros datos no la cambia.
                    </span>
                  </div>
                )
              ) : (
                <p className="text-xs text-slate-400">Selecciona un contratante para registrarlo como el contratante principal de la persona.</p>
              )}
              {/* v2.13.4 · ALCANCE DE SERVICIO + protocolo de no respuesta de Recordatorios.
                  `Paquete` sigue siendo la etiqueta comercial y NO decide qué se exige: el alcance
                  es esta lista estructurada. Sin ninguna casilla marcada la persona conserva su
                  comportamiento legacy. */}
              <div className="rounded-xl border border-gray-100 p-3 space-y-2" data-testid="persona-servicios">
                <p className="text-sm font-medium text-[#012472]">Servicios contratados</p>
                <p className="text-xs text-slate-400">Determina qué requisitos se exigen a esta persona. Si no marcas ninguno se conserva el criterio anterior.</p>
                {SERVICIOS.map(([v, label, hint]) => (
                  <label key={v} className="flex items-start gap-2 text-sm text-slate-600">
                    <Checkbox data-testid={`persona-servicio-${v}`} checked={servicios.includes(v)} onCheckedChange={() => toggleServicio(v)} />
                    <span><span className="font-medium text-slate-700">{label}</span><br /><span className="text-xs text-slate-400">{hint}</span></span>
                  </label>
                ))}
              </div>
              {servicios.includes("recordatorios") && (
                <div className="rounded-xl border border-gray-100 p-3 space-y-3" data-testid="persona-politica-recordatorios">
                  <p className="text-sm font-medium text-[#012472]">Si la persona no responde al recordatorio</p>
                  <p className="text-xs text-slate-400">Galarya comunica el recordatorio; nunca afirma que el medicamento se tomó. El cierre es factual.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Intentos de llamada">
                      <Select value={pol.intentos_llamada ? String(pol.intentos_llamada) : ""} onValueChange={(v) => setPol("intentos_llamada", Number(v))}>
                        <SelectTrigger className="rounded-xl" data-testid="politica-intentos"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n} intento(s)</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Enviar WhatsApp tras la no respuesta" hint="Paso operativo manual por el canal autorizado; no hay envío automático.">
                      <YesNoSelect value={pol.whatsapp_tras_no_respuesta} onChange={(v) => setPol("whatsapp_tras_no_respuesta", v)} testId="politica-whatsapp" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Notificar a un contacto tras la no respuesta">
                      <YesNoSelect value={pol.notificar_contacto} onChange={(v) => setPol("notificar_contacto", v)} testId="politica-notificar" />
                    </Field>
                    {pol.notificar_contacto === true && (
                      <Field label="Contacto designado">
                        <Select value={pol.contacto_designado_id || ""} onValueChange={(v) => setPol("contacto_designado_id", v)}>
                          <SelectTrigger className="rounded-xl" data-testid="politica-contacto-designado"><SelectValue placeholder="Elige entre sus contactos" /></SelectTrigger>
                          <SelectContent>
                            {opcionesContactos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre_completo || "(sin nombre)"}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                    )}
                  </div>
                  <Field label="Resultado que se registra al cerrar" hint="Ej. Recordatorio comunicado / Recordatorio no confirmado / Contacto designado notificado.">
                    <Input className="rounded-xl" data-testid="politica-resultado" placeholder="Ej. Recordatorio comunicado o, si no hubo respuesta, recordatorio no confirmado"
                      value={pol.resultado_esperado || ""} onChange={(e) => setPol("resultado_esperado", e.target.value)} />
                  </Field>
                </div>
              )}
              {!persona?.id && (
              <Field label="Estado">
                <Select value={f.estado} onValueChange={(v) => set("estado", v)}>
                  <SelectTrigger className="rounded-xl" data-testid="persona-estado-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Activa">Activa</SelectItem>
                    <SelectItem value="Inactiva">Inactiva</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              )}
            </AccordionContent>
          </AccordionItem>)}

          {/* 2. Contactos */}
          {secs.includes("contactos") && (<AccordionItem value="contactos">
            <AccordionTrigger className="text-[#012472]">Contactos de escalamiento</AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              {/* v2.13.4: el mínimo lo fija el backend SEGÚN EL SERVICIO contratado. Recordatorios
                  opera con un contacto designado; el monitoreo por sensores conserva el mínimo 2. */}
              <p className="text-xs text-slate-400">
                {soloRecordatorios
                  ? "Recordatorios: sólo se exige un contacto designado si la política notifica a un tercero tras la no respuesta."
                  : "Mínimo requerido: 2 contactos de escalamiento. Puedes guardar con cualquier número (incluido cero); la persona queda como borrador hasta cumplirlo."}
              </p>
              <p className="text-[11px] text-slate-400">Sólo se muestran los contactos vinculados a esta persona.</p>
              {(f.contactos || []).map((row, i) => (
                <div key={i} className="rounded-xl border border-gray-100 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">Contacto {i + 1}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={() => delContactoRow(i)}>
                      <Trash2 strokeWidth={1.5} className="h-4 w-4" />
                    </Button>
                  </div>
                  <Select value={row.contacto_id || ""} onValueChange={(v) => updContactoRow(i, "contacto_id", v)}>
                    <SelectTrigger data-testid={`persona-contacto-select-${i}`} className="rounded-xl"><SelectValue placeholder="Selecciona contacto" /></SelectTrigger>
                    <SelectContent>
                      {opcionesContactos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre_completo || "(sin nombre)"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-2">
                    <Input className="rounded-xl" placeholder="Relación familiar" value={row.relacion_familiar || ""} onChange={(e) => updContactoRow(i, "relacion_familiar", e.target.value)} />
                    <Select value={String(row.orden || "")} onValueChange={(v) => updContactoRow(i, "orden", Number(v))}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Orden" /></SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>Orden {n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input className="rounded-xl" placeholder="Ej. sólo recibe avisos / puede autorizar cambios / puede autorizar despacho" value={row.autoridad || ""} onChange={(e) => updContactoRow(i, "autoridad", e.target.value)} />
                </div>
              ))}
              {errorContactos && (
                <p data-testid="persona-contactos-error" className="text-xs text-red-600">{errorContactos}</p>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="rounded-xl" onClick={addContactoRow} data-testid="add-contacto-row">
                  <Plus strokeWidth={1.5} className="h-4 w-4 mr-1" /> Agregar contacto
                </Button>
                {puedeCrearContacto && (
                  <Button variant="ghost" className="rounded-xl text-[#01CBE3]" onClick={() => setContactoDlg(true)} data-testid="registrar-nuevo-contacto">
                    <Plus strokeWidth={1.5} className="h-4 w-4 mr-1" /> Nuevo contacto
                  </Button>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>)}

          {/* 3. Instrucciones operativas */}
          {secs.includes("instrucciones") && (<AccordionItem value="instrucciones">
            <AccordionTrigger className="text-[#012472]">Instrucciones operativas</AccordionTrigger>
            <AccordionContent className="space-y-5 pt-2">
              <Field label="1. Autoridad: ¿quién decide?">
                <Select value={f.instrucciones.autoridad.tipo || ""} onValueChange={(v) => setInstr("autoridad", { ...f.instrucciones.autoridad, tipo: v })}>
                  <SelectTrigger data-testid="autoridad-select" className="rounded-xl"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="propia_persona">La propia persona</SelectItem>
                    <SelectItem value="representante_legal">Representante legal</SelectItem>
                    <SelectItem value="otra_persona_autorizada">Otra persona autorizada</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {f.instrucciones.autoridad.tipo && f.instrucciones.autoridad.tipo !== "propia_persona" && (
                <div className="grid grid-cols-2 gap-3">
                  <Input className="rounded-xl" placeholder="Nombre" value={f.instrucciones.autoridad.nombre} onChange={(e) => setInstr("autoridad", { ...f.instrucciones.autoridad, nombre: e.target.value })} />
                  <Input className="rounded-xl" placeholder="Relación" value={f.instrucciones.autoridad.relacion} onChange={(e) => setInstr("autoridad", { ...f.instrucciones.autoridad, relacion: e.target.value })} />
                </div>
              )}

              <Field label="2. Autorización de emergencia" hint="Galarya puede contactar servicios de emergencia según el protocolo, sin esperar autorización familiar.">
                <YesNoFecha obj={f.instrucciones.autorizacion_emergencia} onChange={(v) => setInstr("autorizacion_emergencia", v)} testId="autorizacion-emergencia" />
              </Field>

              <Field label="3. Si la persona rechaza emergencia — instrucción a seguir">
                <Textarea className="rounded-xl" value={f.instrucciones.instruccion_rechazo_emergencia} onChange={(e) => setInstr("instruccion_rechazo_emergencia", e.target.value)} />
              </Field>

              <Field label="4. Directiva anticipada / cuidados paliativos: ¿existe?">
                <YesNoSelect value={f.instrucciones.directiva_anticipada.existe} onChange={(v) => setInstr("directiva_anticipada", { ...f.instrucciones.directiva_anticipada, existe: v })} />
              </Field>
              {f.instrucciones.directiva_anticipada.existe === true && (
                <div className="space-y-3">
                  <Input className="rounded-xl" placeholder="Responsable designado" value={f.instrucciones.directiva_anticipada.responsable} onChange={(e) => setInstr("directiva_anticipada", { ...f.instrucciones.directiva_anticipada, responsable: e.target.value })} />
                  <Textarea className="rounded-xl" placeholder="Instrucción operativa concreta (el operador solo lee, no interpreta)" value={f.instrucciones.directiva_anticipada.instruccion_operativa} onChange={(e) => setInstr("directiva_anticipada", { ...f.instrucciones.directiva_anticipada, instruccion_operativa: e.target.value })} />
                </div>
              )}

              {/* 5. Quien modifica protocolo — destacado */}
              <div className="rounded-xl border-2 border-[#01CBE3]/40 bg-[#01CBE3]/5 p-4 space-y-2">
                <div className="flex items-center gap-2 text-[#012472] font-medium text-sm">
                  <ShieldAlert strokeWidth={1.75} className="h-4 w-4 text-[#01CBE3]" />
                  5. Titular del protocolo (autoriza cambios)
                </div>
                <p className="text-xs text-slate-500">
                  Sólo esta persona puede cambiar las instrucciones. Ser contacto de escalamiento no otorga esa autoridad.
                </p>
                <HybridSelect value={f.instrucciones.quien_modifica_protocolo} onChange={(v) => setInstr("quien_modifica_protocolo", v)} contactos={opcionesContactos} testId="quien-modifica-select" />
              </div>

              <Field label="6. En decisiones en conflicto, ¿quién prevalece? (instrucción operativa)">
                <HybridSelect value={f.instrucciones.conflicto_familiares} onChange={(v) => setInstr("conflicto_familiares", v)} contactos={opcionesContactos} testId="conflicto-familiares-select" />
              </Field>

              <Field label="7. ¿Verificador físico contratado?">
                <YesNoSelect value={f.instrucciones.verificador_fisico.contratado} onChange={(v) => setInstr("verificador_fisico", { ...f.instrucciones.verificador_fisico, contratado: v })} />
              </Field>
              {f.instrucciones.verificador_fisico.contratado === true && (
                <Field label="¿Quién puede autorizar un despacho?">
                  <HybridSelect value={f.instrucciones.verificador_fisico.autoriza} onChange={(v) => setInstr("verificador_fisico", { ...f.instrucciones.verificador_fisico, autoriza: v })} contactos={opcionesContactos} testId="verificador-autoriza-select" />
                </Field>
              )}

              <div className="space-y-3">
                <p className="text-sm font-medium text-[#012472]">8. Acceso al inmueble</p>
                <Input className="rounded-xl" placeholder="Ubicación y código de caja de llaves" value={f.instrucciones.acceso_inmueble.ubicacion_codigo_caja} onChange={(e) => setInstr("acceso_inmueble", { ...f.instrucciones.acceso_inmueble, ubicacion_codigo_caja: e.target.value })} />
                <Input className="rounded-xl" placeholder="Caseta de acceso" value={f.instrucciones.acceso_inmueble.caseta} onChange={(e) => setInstr("acceso_inmueble", { ...f.instrucciones.acceso_inmueble, caseta: e.target.value })} />
                <Input className="rounded-xl" placeholder="Puertas adicionales" value={f.instrucciones.acceso_inmueble.puertas_adicionales} onChange={(e) => setInstr("acceso_inmueble", { ...f.instrucciones.acceso_inmueble, puertas_adicionales: e.target.value })} />
                <Input className="rounded-xl" placeholder="Mascotas" value={f.instrucciones.acceso_inmueble.mascotas} onChange={(e) => setInstr("acceso_inmueble", { ...f.instrucciones.acceso_inmueble, mascotas: e.target.value })} />
                <Input className="rounded-xl" placeholder="Obstáculos conocidos" value={f.instrucciones.acceso_inmueble.obstaculos} onChange={(e) => setInstr("acceso_inmueble", { ...f.instrucciones.acceso_inmueble, obstaculos: e.target.value })} />
              </div>

              <Field label="9. Consentimiento de audio bidireccional" hint="Si está en NO, el paso de verificación por audio no aplica. Un alta puede quedar activa con este consentimiento en NO.">
                <YesNoFecha obj={f.instrucciones.consentimiento_audio_bidireccional} onChange={(v) => setInstr("consentimiento_audio_bidireccional", v)} testId="consent-audio" />
              </Field>

              <Field label="10. Consentimiento de grabación de audio">
                <YesNoFecha obj={f.instrucciones.consentimiento_grabacion} onChange={(v) => setInstr("consentimiento_grabacion", v)} testId="consent-grabacion" />
              </Field>

              <Field label="11. Confirmación de límites" hint={soloRecordatorios
                ? "La persona/cliente entiende que Galarya comunica el recordatorio e intenta confirmarlo según el protocolo acordado, y que haber generado o comunicado un recordatorio no permite afirmar que la persona realizó la acción ni que tomó el medicamento."
                : "El cliente entiende que los sensores pueden producir falsas alarmas, no sustituyen cuidador ni atención médica, y la cobertura se limita a lo instalado."}>
                <YesNoFecha obj={f.instrucciones.confirmacion_limites} onChange={(v) => setInstr("confirmacion_limites", v)} testId="confirmacion-limites" />
              </Field>
            </AccordionContent>
          </AccordionItem>)}

          {/* 4. Dispositivos */}
          {secs.includes("dispositivos") && (<AccordionItem value="dispositivos">
            <AccordionTrigger className="text-[#012472]">Dispositivos que la monitorean</AccordionTrigger>
            <AccordionContent className="space-y-2 pt-2">
              {(dispositivos || []).length === 0 && <p className="text-sm text-slate-400">No hay dispositivos en este sitio todavía.</p>}
              {(dispositivos || []).map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox checked={selDevices.includes(d.id)} onCheckedChange={() => toggleDevice(d.id)} data-testid={`persona-device-${d.id}`} />
                  {d.tipo || "Dispositivo"} — {d.ubicacion || "sin ubicación"}
                </label>
              ))}
            </AccordionContent>
          </AccordionItem>)}
        </Accordion>

        <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-white/95 backdrop-blur border-t border-gray-100">
          {errorGuardado && (
            <div role="alert" data-testid="persona-error-banner" className="w-full rounded-xl border border-red-200 bg-red-50 p-3 text-left">
              <p className="text-sm font-semibold text-red-700">{errorGuardado.titulo}</p>
              <p className="text-xs text-red-600 mt-0.5">{errorGuardado.detalle}</p>
              <p className="text-xs text-red-500 mt-1">Tu captura sigue en pantalla; no se perdió nada.</p>
              {errorGuardado.incierto && (
                <Button variant="outline" size="sm" data-testid="persona-consultar-estado" className="mt-2 rounded-lg"
                  onClick={() => consultarEstadoIncierto(() => onSaved && onSaved())}>Consultar estado</Button>
              )}
            </div>
          )}
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="persona-guardar-button" disabled={saving} aria-busy={saving} className={`rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white ${saving ? "pointer-events-none" : ""}`} onClick={save}>
            Guardar
          </Button>
        </DialogFooter>

        <ContactForm
          open={contactoDlg}
          onOpenChange={setContactoDlg}
          contacto={null}
          personaId={persona?.id || null}
          onSaved={(nuevo, relaciones) => (persona?.id
            ? contactoCreadoEnPersona(nuevo, relaciones)
            : vincularContactoNuevo(nuevo))}
        />
      </DialogContent>
    </Dialog>
  );
}
